import type { QueryOptions } from '@tanstack/react-query';

import { http } from '@/api/instance';
import { getQueryParameters } from '@/api/query';

export const employeeAPI = {
  list: ({ ...params }: Record<string, string | number>) =>
    http.get<ListResultsPageSize<Employee>>('/api/info/employee', { params }).then(({ data }) => data),
  create: (data: EmployeeFormPayload) => http.post<unknown>(`/api/add/employee`, { data }),
  detail: (id: number | string) => http.get<Employee>(`/api/info/employee/${id}`).then(({ data }) => data),
  update: (id: number | string, data: EmployeeFormPayload) =>
    http.put(`/api/edit/employee/${id}`, { data }).then(({ data }) => data),
  delete: (id: number | string) => http.delete<unknown>(`/api/delete/employee/${id}`),

  list_teacher: ({ ...params }: Record<string, string | number>) =>
    http.get<ListResultsPageSize<Teacher>>('/api/info/teacher', { params }).then(({ data }) => data),
  list_available_teacher: (params: AvailableTeacherParams) =>
    http.get<ListResultsPageSize<AvailableTeacher>>('api/info/teacher/available', { params }).then(({ data }) => data),
  classes: (employee_id: string | number) =>
    http.get<{ data: ClassByTeacher[] }>(`/api/info/employee/${employee_id}/class`).then(({ data }) => data.data),
  classSession: (employee_id: string | number, class_id: string | number) =>
    http
      .get<{ data: ClassSession[] }>(`/api/info/employee/${employee_id}/class/${class_id}/session`)
      .then(({ data }) => data.data),

  transfer_data: (data: TransferEmployeeDataPayload) => http.put<unknown>('/api/transfer/employee', { data }),
};

export const employeeQuery = {
  list: (params: QueryParams<Partial<SearchFormData & { user_role_id: number | string }>>) =>
    ({
      queryKey: ['GetEmployee', params] as const,
      queryFn: () => employeeAPI.list(getQueryParameters(params)),
    }) as const satisfies QueryOptions,
  classes: (employee_id: string | number) =>
    ({
      queryKey: ['GetListClasses', employee_id] as const,
      queryFn: () => employeeAPI.classes(employee_id),
    }) as const satisfies QueryOptions,
  classSession: (employee_id: string | number, class_id: string | number) =>
    ({
      queryKey: ['GetListClassSession', employee_id, class_id] as const,
      queryFn: () => employeeAPI.classSession(employee_id, class_id),
    }) as const satisfies QueryOptions,
  detail: (id: number | string) =>
    ({
      queryKey: ['GetEmployeeDetail', id] as const,
      queryFn: () => employeeAPI.detail(id),
    }) as const satisfies QueryOptions,
  list_teacher: (params: QueryParams<Partial<SearchFormData & { type: 'TEACHER' | 'TA' }>>) =>
    ({
      queryKey: ['GetTeacher', params] as const,
      queryFn: () => employeeAPI.list_teacher(getQueryParameters(params)),
    }) as const satisfies QueryOptions,
  list_available_teacher: (params: AvailableTeacherParams) =>
    ({
      queryKey: ['GetAvailableTeacher', params] as const,
      queryFn: () => employeeAPI.list_available_teacher(params),
    }) as const satisfies QueryOptions,
};
